import { useMemo, useState } from "react";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import Modal from "@/components/base/Modal";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { TERRITORY_LEVEL_LABELS, type TerritoryLevel } from "@/store/territory/types";
import { useTerritory } from "@/store/territory/TerritoryProvider";
import type { ExportColumn } from "@/utils/export";
import { cn } from "@/utils/cn";
import TerritoryForm, { type TerritoryFormNode } from "./TerritoryForm";

interface TerritoryRow {
  id: string;
  nom: string;
  parentLabel: string;
  provinces?: number;
  communes?: number;
  secteurs?: number;
  immeubles: number;
  childCount: number;
  childNoun: string;
  children: string[];
  parentId: string;
}

interface LevelConfig {
  heading: string;
  intro: string;
  icon: string;
  addLabel: string;
}

const LEVEL_CONFIG: Record<TerritoryLevel, LevelConfig> = {
  region: {
    heading: "Régions",
    intro:
      "Découpage territorial de premier niveau. Chaque région regroupe ses provinces, communes et secteurs. Source de vérité unique utilisée par toute la plateforme.",
    icon: "ri-map-2-line",
    addLabel: "Ajouter une région",
  },
  province: {
    heading: "Provinces",
    intro:
      "Chaque province appartient à une région. Les communes et secteurs en dépendent : la cascade garantit une sélection toujours cohérente.",
    icon: "ri-community-line",
    addLabel: "Ajouter une province",
  },
  commune: {
    heading: "Communes / Villes",
    intro:
      "Chaque commune / ville appartient à une province (donc à une région). Les filtres fonctionnent en cascade : Région → Province → Commune.",
    icon: "ri-map-pin-2-line",
    addLabel: "Ajouter une commune / ville",
  },
  secteur: {
    heading: "Secteurs / Quartiers",
    intro:
      "Dernier niveau du découpage : chaque secteur / quartier appartient à une commune. Les sélections des niveaux parents limitent les choix disponibles.",
    icon: "ri-road-map-line",
    addLabel: "Ajouter un secteur / quartier",
  },
};

interface TerritoryModuleProps {
  level: TerritoryLevel;
}

/**
 * Module territorial générique pour les quatre niveaux.
 * Consultation, recherche, cascade et exports fonctionnent de façon identique ;
 * seules les colonnes et les protections de suppression changent selon le niveau.
 */
export default function TerritoryModule({ level }: TerritoryModuleProps) {
  const territory = useTerritory();
  const { immeubles } = usePlatformData();
  const config = LEVEL_CONFIG[level];

  const [search, setSearch] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [communeFilter, setCommuneFilter] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TerritoryFormNode | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TerritoryRow | null>(null);

  const immeublesBy = (predicate: (item: (typeof immeubles)[number]) => boolean) =>
    immeubles.filter(predicate).length;

  const rows = useMemo<TerritoryRow[]>(() => {
    if (level === "region") {
      return territory.regions.map((region) => {
        const provinces = territory.provincesOf(region.id);
        return {
          id: region.id,
          nom: region.nom,
          parentId: "",
          parentLabel: "",
          provinces: provinces.length,
          communes: territory.communesOfRegion(region.id).length,
          secteurs: territory.secteursOfRegion(region.id).length,
          immeubles: immeublesBy((item) => item.region === region.nom),
          childCount: provinces.length,
          childNoun: "province",
          children: provinces.map((item) => item.nom),
        };
      });
    }
    if (level === "province") {
      return territory.provinces.map((province) => {
        const communes = territory.communesOf(province.id);
        return {
          id: province.id,
          nom: province.nom,
          parentId: province.regionId,
          parentLabel: territory.regionName(province.regionId),
          communes: communes.length,
          secteurs: territory.secteursOfProvince(province.id).length,
          immeubles: immeublesBy((item) => item.province === province.nom),
          childCount: communes.length,
          childNoun: "commune",
          children: communes.map((item) => item.nom),
        };
      });
    }
    if (level === "commune") {
      return territory.communes.map((commune) => {
        const secteurs = territory.secteurOf(commune.id);
        const province = territory.getProvince(commune.provinceId);
        return {
          id: commune.id,
          nom: commune.nom,
          parentId: commune.provinceId,
          parentLabel: province ? `${province.nom} · ${territory.regionName(province.regionId)}` : "",
          secteurs: secteurs.length,
          immeubles: immeublesBy((item) => item.commune === commune.nom),
          childCount: secteurs.length,
          childNoun: "secteur",
          children: secteurs.map((item) => item.nom),
        };
      });
    }
    return territory.secteurs.map((secteur) => {
      const commune = territory.getCommune(secteur.communeId);
      const province = commune ? territory.getProvince(commune.provinceId) : undefined;
      return {
        id: secteur.id,
        nom: secteur.nom,
        parentId: secteur.communeId,
        parentLabel: commune
          ? `${commune.nom}${province ? ` · ${province.nom}` : ""}`
          : "",
        immeubles: immeublesBy((item) => item.secteur === secteur.nom),
        childCount: 0,
        childNoun: "élément",
        children: [],
      };
    });
  }, [level, territory, immeubles]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((row) => {
        if (term && !`${row.nom} ${row.parentLabel}`.toLowerCase().includes(term)) return false;
        if (regionFilter && level !== "region") {
          const provinceId = level === "province" ? row.id : level === "commune" ? territory.getProvince(row.parentId)?.regionId : undefined;
          if (level === "province" && row.parentId !== regionFilter) return false;
          if (level === "commune" && provinceId !== regionFilter) return false;
          if (level === "secteur") {
            const commune = territory.getCommune(row.parentId);
            const province = commune ? territory.getProvince(commune.provinceId) : undefined;
            if (!province || province.regionId !== regionFilter) return false;
          }
        }
        if (provinceFilter && level !== "region" && level !== "province") {
          if (level === "commune" && row.parentId !== provinceFilter) return false;
          if (level === "secteur") {
            const commune = territory.getCommune(row.parentId);
            if (!commune || commune.provinceId !== provinceFilter) return false;
          }
        }
        if (communeFilter && level === "secteur" && row.parentId !== communeFilter) return false;
        return true;
      })
      .sort((a, b) => a.nom.localeCompare(b.nom, "fr"));
  }, [rows, search, regionFilter, provinceFilter, communeFilter, level, territory]);

  const isFiltering = Boolean(search.trim() || regionFilter || provinceFilter || communeFilter);

  const resetFilters = () => {
    setSearch("");
    setRegionFilter("");
    setProvinceFilter("");
    setCommuneFilter("");
  };

  const stats = useMemo(
    () => ({
      total: rows.length,
      immeubles: rows.reduce((sum, row) => sum + row.immeubles, 0),
      withChildren: rows.filter((row) => row.childCount > 0).length,
      empty: rows.filter((row) => row.childCount === 0).length,
    }),
    [rows]
  );

  const detailRow = detailId ? rows.find((row) => row.id === detailId) ?? null : null;

  const confirmDelete = () => {
    if (!pendingDelete || pendingDelete.childCount > 0) return;
    if (level === "region") territory.removeRegion(pendingDelete.id);
    else if (level === "province") territory.removeProvince(pendingDelete.id);
    else if (level === "commune") territory.removeCommune(pendingDelete.id);
    else territory.removeSecteur(pendingDelete.id);
    setPendingDelete(null);
  };

  const exportColumns = useMemo<ExportColumn<TerritoryRow>[]>(() => {
    const base: ExportColumn<TerritoryRow>[] = [{ header: TERRITORY_LEVEL_LABELS[level], value: (row) => row.nom }];
    if (level !== "region") base.push({ header: "Rattachement", value: (row) => row.parentLabel });
    if (level === "region") base.push({ header: "Provinces", value: (row) => row.provinces ?? 0 });
    if (level === "region" || level === "province") base.push({ header: "Communes / Villes", value: (row) => row.communes ?? 0 });
    if (level !== "secteur") base.push({ header: "Secteurs / Quartiers", value: (row) => row.secteurs ?? 0 });
    base.push({ header: "Immeubles liés", value: (row) => row.immeubles });
    return base;
  }, [level]);

  const blockedMessage = pendingDelete
    ? pendingDelete.childCount > 0
      ? `« ${pendingDelete.nom} » contient ${pendingDelete.childCount} ${pendingDelete.childNoun}${
          pendingDelete.childCount > 1 ? "s" : ""
        }. Supprimez ou déplacez d'abord ces éléments pour préserver la cascade territoriale.`
      : pendingDelete.immeubles > 0
        ? `« ${pendingDelete.nom} » est référencé par ${pendingDelete.immeubles} immeuble${
            pendingDelete.immeubles > 1 ? "s" : ""
          }. Modifiez d'abord ces immeubles pour éviter des données orphelines.`
        : undefined
    : undefined;

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: TerritoryRow) => {
    setEditing({ id: row.id, nom: row.nom, parentId: row.parentId });
    setFormOpen(true);
  };

  const showRegionFilter = level !== "region";
  const showProvinceFilter = level === "commune" || level === "secteur";
  const showCommuneFilter = level === "secteur";

  const regionOptions = [
    { value: "", label: "Toutes les régions" },
    ...territory.regions.map((region) => ({ value: region.id, label: region.nom })),
  ];
  const provinceOptions = [
    { value: "", label: "Toutes les provinces" },
    ...territory.provinces
      .filter((province) => !regionFilter || province.regionId === regionFilter)
      .map((province) => ({ value: province.id, label: province.nom })),
  ];
  const communeOptions = [
    { value: "", label: "Toutes les communes / villes" },
    ...territory.communes
      .filter((commune) => !provinceFilter || commune.provinceId === provinceFilter)
      .map((commune) => ({ value: commune.id, label: commune.nom })),
  ];

  const gridCols = level === "secteur" ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-2 xl:grid-cols-4";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{config.heading}</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{config.intro}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename={`super-admin-${level}`}
            title={config.heading}
            subtitle="Données actuellement affichées"
          />
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            {config.addLabel}
          </button>
        </div>
      </div>

      <div className={cn("grid grid-cols-1 gap-4", gridCols)}>
        <StatCard icon={config.icon} label={config.heading} value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-map-pin-2-line" label="Immeubles liés" value={String(stats.immeubles)} tone="secondary" />
        <StatCard icon="ri-node-tree" label="Avec subdivisions" value={String(stats.withChildren)} tone="accent" />
        <StatCard icon="ri-checkbox-blank-circle-line" label="Sans subdivision" value={String(stats.empty)} tone="neutral" />
      </div>

      {rows.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`terr-search-${level}`} className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id={`terr-search-${level}`}
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom..."
              />
            </div>
            {showRegionFilter && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`terr-region-${level}`} className="text-xs font-label font-medium text-foreground-700">
                  Région
                </label>
                <Select
                  id={`terr-region-${level}`}
                  value={regionFilter}
                  onChange={(event) => {
                    setRegionFilter(event.target.value);
                    setProvinceFilter("");
                    setCommuneFilter("");
                  }}
                  options={regionOptions}
                />
              </div>
            )}
            {showProvinceFilter && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`terr-province-${level}`} className="text-xs font-label font-medium text-foreground-700">
                  Province
                </label>
                <Select
                  id={`terr-province-${level}`}
                  value={provinceFilter}
                  onChange={(event) => {
                    setProvinceFilter(event.target.value);
                    setCommuneFilter("");
                  }}
                  options={provinceOptions}
                  disabled={!regionFilter}
                />
              </div>
            )}
            {showCommuneFilter && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`terr-commune-${level}`} className="text-xs font-label font-medium text-foreground-700">
                  Commune / Ville
                </label>
                <Select
                  id={`terr-commune-${level}`}
                  value={communeFilter}
                  onChange={(event) => setCommuneFilter(event.target.value)}
                  options={communeOptions}
                  disabled={!provinceFilter}
                />
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} élément{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {rows.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {territory.loading && rows.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!territory.loading && territory.error && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {territory.error}
          </p>
          <button
            type="button"
            onClick={() => territory.reload()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!territory.loading && !territory.error && rows.length === 0 && (
        <EmptyState
          icon={config.icon}
          title={`Aucun élément — ${config.heading}`}
          description="Ce niveau territorial est vide. Ajoutez un premier élément pour commencer."
          action={
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              {config.addLabel}
            </button>
          }
        />
      )}

      {rows.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun élément ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60 text-left">
                  <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                    {TERRITORY_LEVEL_LABELS[level]}
                  </th>
                  {level !== "region" && (
                    <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                      Rattachement
                    </th>
                  )}
                  {level === "region" && (
                    <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                      Provinces
                    </th>
                  )}
                  {(level === "region" || level === "province") && (
                    <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                      Communes
                    </th>
                  )}
                  {level !== "secteur" && (
                    <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                      Secteurs
                    </th>
                  )}
                  <th scope="col" className="px-4 py-3 font-label font-semibold text-foreground-500">
                    Immeubles
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-label font-semibold text-foreground-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-background-200 last:border-0 hover:bg-background-100/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-label font-medium text-foreground-950">{row.nom}</span>
                        {row.childCount > 0 && (
                          <Badge tone="neutral">{`${row.childCount} ${row.childNoun}${row.childCount > 1 ? "s" : ""}`}</Badge>
                        )}
                      </div>
                    </td>
                    {level !== "region" && (
                      <td className="px-4 py-3 text-foreground-600">{row.parentLabel || "—"}</td>
                    )}
                    {level === "region" && <td className="px-4 py-3 text-foreground-600">{row.provinces ?? 0}</td>}
                    {(level === "region" || level === "province") && (
                      <td className="px-4 py-3 text-foreground-600">{row.communes ?? 0}</td>
                    )}
                    {level !== "secteur" && <td className="px-4 py-3 text-foreground-600">{row.secteurs ?? 0}</td>}
                    <td className="px-4 py-3 text-foreground-600">{row.immeubles}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setDetailId(row.id)}
                          title="Voir le détail"
                          aria-label="Voir le détail"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-foreground-200 bg-background-50 text-foreground-700 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-eye-line text-base" aria-hidden="true" />
                        </button>
                        <CardActions onEdit={() => openEdit(row)} onDelete={() => setPendingDelete(row)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <TerritoryForm level={level} open={formOpen} onClose={() => setFormOpen(false)} node={editing} />

      <Modal open={Boolean(detailRow)} onClose={() => setDetailId(null)} size="md" title={detailRow?.nom}>
        {detailRow && (
          <div className="flex flex-col gap-4">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {level !== "region" && (
                <div>
                  <dt className="text-xs text-foreground-500">Rattachement</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">{detailRow.parentLabel || "—"}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-foreground-500">Immeubles liés</dt>
                <dd className="text-sm font-label font-medium text-foreground-950">{detailRow.immeubles}</dd>
              </div>
              {level === "region" && (
                <div>
                  <dt className="text-xs text-foreground-500">Provinces</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">{detailRow.provinces ?? 0}</dd>
                </div>
              )}
              {(level === "region" || level === "province") && (
                <div>
                  <dt className="text-xs text-foreground-500">Communes / Villes</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">{detailRow.communes ?? 0}</dd>
                </div>
              )}
              {level !== "secteur" && (
                <div>
                  <dt className="text-xs text-foreground-500">Secteurs / Quartiers</dt>
                  <dd className="text-sm font-label font-medium text-foreground-950">{detailRow.secteurs ?? 0}</dd>
                </div>
              )}
            </dl>

            {detailRow.children.length > 0 && (
              <div>
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Subdivisions
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {detailRow.children.map((child) => (
                    <span
                      key={child}
                      className="rounded-full border border-background-200 bg-background-100 px-2.5 py-1 text-xs text-foreground-700"
                    >
                      {child}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={pendingDelete ? `Supprimer « ${pendingDelete.nom} » ? Cette action est définitive.` : ""}
        blockedMessage={blockedMessage}
      />
    </div>
  );
}